import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { CREATE_BANNER_MUTATION } from '../../apollo/queries';
import { uploadFileToS3, validateFile, resizeImage } from '../../utils/s3Upload';
import '../css/AddBannerPage.css';

const AddBannerPage = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    // GraphQL mutation hook
    const [createBanner] = useMutation(CREATE_BANNER_MUTATION, {
        onCompleted: (data) => {
            alert('배너가 성공적으로 저장되었습니다.');
            navigate('/admin/design');
        },
        onError: (error) => {
            alert(`배너 저장 실패: ${error.message}`);
        }
    });
    
    // 폼 상태 관리
    const [formData, setFormData] = useState({
        position: '첫번째',
        linkUrl: '',
        width: '',
        height: '',
        backgroundColor: '',
        bannerText: '',
        order: '',
        isVisible: false
    });
    
    // 파일 관련 상태
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState('선택한 파일 없음');
    const [uploadedImageUrl, setUploadedImageUrl] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');

    // 입력값 변경 처리
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // 파일 선택 처리
    const handleFileSelect = () => {
        fileInputRef.current?.click();
    };

    // 참고코드와 동일한 파일 업로드 처리
    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const file = files[0]; // 단일 파일만 처리
        
        try {
            setSelectedFile(file);
            setFileName(file.name);
            setIsUploading(true);
            setUploadedImageUrl('');
            
            // 로컬 미리보기 생성 (참고코드 방식)
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviewUrl(e.target.result);
            };
            reader.readAsDataURL(file);

            // S3 업로드 (참고코드와 동일한 방식)
            const s3Url = await uploadFileToS3(file);
            if (s3Url) {
                setUploadedImageUrl(s3Url);
                // 업로드 완료 후 미리보기도 S3 URL로 변경 (참고코드 방식)
                setPreviewUrl(s3Url);
                console.log('파일이 성공적으로 업로드되었습니다.');
            } else {
                alert('파일 업로드에 실패했습니다.');
            }
            
        } catch (error) {
            console.error('파일 업로드 에러:', error);
            alert(`파일 업로드 실패: ${error.message}`);
            // 업로드 실패 시 초기화
            e.target.value = '';
            setSelectedFile(null);
            setFileName('선택한 파일 없음');
            setPreviewUrl('');
        } finally {
            setIsUploading(false);
        }
    };

    // 이미지 클릭 시 새 탭에서 열기 (참고코드와 동일)
    const handleImageClick = () => {
        if (uploadedImageUrl) {
            window.open(uploadedImageUrl, '_blank');
        }
    };

    // 이미지 제거 함수 (참고코드 방식)
    const handleRemoveImage = () => {
        setSelectedFile(null);
        setFileName('선택한 파일 없음');
        setUploadedImageUrl('');
        setPreviewUrl('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // 폼 제출 처리
    const handleSubmit = async () => {
        // 필수 필드 검증
        if (!uploadedImageUrl) {
            alert('배너 이미지를 업로드해주세요.');
            return;
        }

        if (!formData.width || !formData.height) {
            alert('배너 크기를 입력해주세요.');
            return;
        }

        // GraphQL mutation 실행
        try {
            await createBanner({
                variables: {
                    input: {
                        position: formData.position,
                        imageUrl: uploadedImageUrl,
                        linkUrl: formData.linkUrl || null,
                        width: formData.width ? parseInt(formData.width) : null,
                        height: formData.height ? parseInt(formData.height) : null,
                        backgroundColor: formData.backgroundColor || null,
                        bannerText: formData.bannerText || null,
                        order: formData.order ? parseInt(formData.order) : 0,
                        isVisible: formData.isVisible
                    }
                }
            });
        } catch (error) {
            console.error('배너 저장 에러:', error);
            // 에러는 onError 콜백에서 처리됨
        }
    };

    return (
        <>
            <div className="breadcrumb">
                <span>홈 &gt; 디자인 관리 &gt; 배너 관리</span>
            </div>
            <div className="admin-main-content">
                <main className="admin-content-add-banner">
                    <div className="page-header">
                        <div className="page-title-bar"></div>
                        <h2>배너 추가</h2>
                    </div>

                    <div className="add-banner-form">
                        <div className="form-row">
                            <label>노출 위치</label>
                            <div className="form-field">
                                <select 
                                    name="position" 
                                    value={formData.position} 
                                    onChange={handleInputChange}
                                >
                                    <option value="첫번째">첫번째</option>
                                    <option value="두번째">두번째</option>
                                    <option value="세번째">세번째</option>
                                    <option value="네번째">네번째</option>
                                    <option value="하단">하단</option>
                                </select>
                            </div>
                        </div>
                        
                        <div className="form-row">
                            <label>배너 파일</label>
                            <div className="form-field">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                />
                                <button 
                                    className="btn-file-select" 
                                    onClick={handleFileSelect}
                                    type="button"
                                    disabled={isUploading}
                                    style={{ 
                                        backgroundColor: isUploading ? '#ccc' : undefined,
                                        cursor: isUploading ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {isUploading ? '업로드 중...' : '파일 선택'}
                                </button>
                                <span className="file-name" style={{ marginLeft: '10px' }}>
                                    {fileName}
                                </span>
                                
                                {/* 업로드 상태 및 S3 링크 표시 (참고코드 방식) */}
                                {uploadedImageUrl && (
                                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ color: 'green', fontWeight: 'bold' }}>✓ 업로드 완료</span>
                                        <a 
                                            href={uploadedImageUrl} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            style={{ 
                                                color: '#007bff', 
                                                textDecoration: 'none',
                                                fontSize: '12px'
                                            }}
                                        >
                                            🔗 S3 링크 보기
                                        </a>
                                        <button
                                            type="button"
                                            onClick={handleRemoveImage}
                                            style={{
                                                background: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '2px 8px',
                                                fontSize: '12px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            ✕ 제거
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 미리보기 (참고코드와 동일한 방식) */}
                        {previewUrl && (
                            <div className="form-row">
                                <label>미리보기</label>
                                <div className="form-field">
                                    <div style={{ 
                                        width: '80px',
                                        height: '80px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        cursor: uploadedImageUrl ? 'pointer' : 'default',
                                        position: 'relative'
                                    }}>
                                        <img 
                                            src={previewUrl} 
                                            alt="배너 미리보기" 
                                            style={{ 
                                                width: '100%', 
                                                height: '100%', 
                                                objectFit: 'cover'
                                            }} 
                                            onClick={handleImageClick}
                                        />
                                        {/* 로딩 오버레이 (참고코드 방식) */}
                                        {isUploading && (
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '4px'
                                            }}>
                                                <div style={{ 
                                                    border: '3px solid #f3f3f3',
                                                    borderTop: '3px solid #007bff',
                                                    borderRadius: '50%',
                                                    width: '20px',
                                                    height: '20px',
                                                    animation: 'spin 1s linear infinite'
                                                }}></div>
                                            </div>
                                        )}
                                    </div>
                                    {uploadedImageUrl && (
                                        <div style={{ 
                                            marginTop: '8px', 
                                            fontSize: '12px', 
                                            color: '#666',
                                            fontStyle: 'italic'
                                        }}>
                                            💡 이미지를 클릭하면 S3 원본 링크로 이동합니다
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                         <div className="form-row">
                            <label>링크 주소</label>
                            <div className="form-field">
                                <input 
                                    type="text" 
                                    name="linkUrl"
                                    value={formData.linkUrl}
                                    onChange={handleInputChange}
                                    placeholder="예: https://example.com" 
                                />
                            </div>
                        </div>
                        
                        <div className="form-row">
                            <label>가로 세로 사이즈</label>
                            <div className="form-field size-input-group">
                                <input 
                                    type="text" 
                                    name="width"
                                    value={formData.width}
                                    onChange={handleInputChange}
                                    placeholder="가로"
                                />
                                <span>PX</span>
                                <input 
                                    type="text" 
                                    name="height"
                                    value={formData.height}
                                    onChange={handleInputChange}
                                    placeholder="세로"
                                />
                                <span>PX</span>
                            </div>
                        </div>
                        
                        <div className="form-row">
                            <label>백그라운드 색상</label>
                            <div className="form-field color-input-group">
                                <input 
                                    type="text" 
                                    name="backgroundColor"
                                    value={formData.backgroundColor}
                                    onChange={handleInputChange}
                                    placeholder="#기호없이 색상값 6가지만 입력하세요. 예) F55862" 
                                />
                            </div>
                        </div>
                        
                        <div className="form-row">
                            <label>배너 문구</label>
                            <div className="form-field">
                                <textarea 
                                    className="banner-text-input" 
                                    name="bannerText"
                                    value={formData.bannerText}
                                    onChange={handleInputChange}
                                    placeholder="특정 배너만 문구가 노출되는 부분으로 모든 배너에 노출되지 않습니다."
                                ></textarea>
                            </div>
                        </div>
                        
                        <div className="form-row">
                            <label>순서</label>
                            <div className="form-field">
                                <input 
                                    type="text" 
                                    name="order"
                                    value={formData.order}
                                    onChange={handleInputChange}
                                    placeholder="순서가 적용안될 우선 순위로 노출 됩니다." 
                                />
                            </div>
                        </div>
                        
                         <div className="form-row">
                            <label>노출 여부</label>
                            <div className="form-field">
                                <label>
                                    <input 
                                        type="checkbox" 
                                        name="isVisible"
                                        checked={formData.isVisible}
                                        onChange={handleInputChange}
                                    /> 노출함
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="form-actions-add-banner">
                        <button className="btn-back-banner" onClick={() => navigate(-1)}> &lt; 돌아가기</button>
                        <button className="btn-save-banner" onClick={handleSubmit}>저장 &gt;</button>
                    </div>
                </main>
            </div>
        </>
    );
};

export default AddBannerPage; 